import type { OfferAttachment } from '../../../types';
import { INLINE_DOCUMENT_IMAGE_MAX_COUNT } from '../../../types';
import {
  computeTargetDimensions,
  downscaleImageFile,
} from '../../../utils/downscaleImage';
import type { DocumentLayoutV1 } from '../../settings/utils/documentLayoutTypes';

/** @deprecated Use INLINE_DOCUMENT_IMAGE_MAX_COUNT. */
export const FIRST_PAGE_IMAGE_MAX_COUNT = INLINE_DOCUMENT_IMAGE_MAX_COUNT;
/** Long-edge cap for PDF embed (not the Claude chat limits). */
export const FIRST_PAGE_IMAGE_MAX_EDGE_PX = 1600;
export const FIRST_PAGE_IMAGE_JPEG_QUALITY = 0.8;

export function isOfferImageAttachment(attachment: { mime_type: string }): boolean {
  return attachment.mime_type === 'image/png' || attachment.mime_type === 'image/jpeg';
}

export function offerAttachmentFileSrc(leadRouteId: string, objectPath: string): string {
  const q = new URLSearchParams({ path: objectPath });
  return `/api/leads/${encodeURIComponent(leadRouteId)}/offer-attachments/file?${q.toString()}`;
}

export function splitAttachmentsForFirstPageGrid<T extends { mime_type: string }>(
  attachments: T[],
  _opts?: { enabled?: boolean; maxFirstPage?: number },
): { firstPage: T[]; merge: T[] } {
  return { firstPage: [], merge: [...attachments] };
}

export function splitOfferAttachmentsForPdf(input: {
  attachments: OfferAttachment[];
  layout?: DocumentLayoutV1 | null;
  skipFirstPageGrid?: boolean;
}): { firstPage: OfferAttachment[]; merge: OfferAttachment[] } {
  return { firstPage: [], merge: [...input.attachments] };
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export async function resolveOfferImageDataUrls(
  leadRouteId: string,
  attachments: OfferAttachment[],
): Promise<Array<{ id: string; src: string; alt: string }>> {
  const resolved = await Promise.all(
    attachments.map(async (attachment) => {
      try {
        const res = await fetch(offerAttachmentFileSrc(leadRouteId, attachment.object_path), {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        const file = new File([blob], attachment.filename, { type: attachment.mime_type });
        const scaled = await downscaleImageFile(file, {
          maxEdge: FIRST_PAGE_IMAGE_MAX_EDGE_PX,
          maxVisualTokens: Number.POSITIVE_INFINITY,
          quality: FIRST_PAGE_IMAGE_JPEG_QUALITY,
        });
        const src = await blobToDataUrl(scaled.file);
        return src ? { id: attachment.id, src, alt: attachment.filename } : null;
      } catch {
        return null;
      }
    }),
  );
  return resolved.filter((row): row is { id: string; src: string; alt: string } => row != null);
}

/** @deprecated Use resolveOfferImageDataUrls. */
export const resolveFirstPageImageDataUrls = resolveOfferImageDataUrls;

/** Exported for tests that assert the PDF resize target without a canvas. */
export function firstPagePdfTargetDimensions(
  width: number,
  height: number,
): { width: number; height: number } | null {
  return computeTargetDimensions(width, height, FIRST_PAGE_IMAGE_MAX_EDGE_PX);
}
